import React, { useEffect, useRef, useState } from 'react';
import { Form, ListGroup, Spinner } from 'react-bootstrap';
import { getPoliciesTrips } from '../../../services/api';
import { mapTripsPolicyPreview } from './commercialGestureUtils';

const pickPolicies = (response) => {
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.policies)) return response.policies;
    if (Array.isArray(response)) return response;
    return [];
};

const PolicyAutocomplete = ({
    value,
    onSelect,
    error,
    disabled = false,
}) => {
    const [query, setQuery] = useState(value?.policy_code || '');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (value?.policy_code) setQuery(value.policy_code);
    }, [value?.policy_code]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const term = query.trim();
        if (term.length < 2 || (value && term === value.policy_code)) {
            setOptions([]);
            return undefined;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const byCode = await getPoliciesTrips({ skip: 0, limit: 10, policy_code: term });
                let rows = pickPolicies(byCode).map(mapTripsPolicyPreview).filter(Boolean);
                if (rows.length === 0) {
                    const byReservation = await getPoliciesTrips({ skip: 0, limit: 10, reservation_number: term });
                    rows = pickPolicies(byReservation).map(mapTripsPolicyPreview).filter(Boolean);
                }
                if (!cancelled) setOptions(rows);
            } catch {
                if (!cancelled) setOptions([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [query, value]);

    const handleChange = (next) => {
        setQuery(next);
        setOpen(true);
        if (value) onSelect(null);
    };

    const handlePick = (option) => {
        setQuery(option.policy_code || '');
        setOpen(false);
        onSelect(option);
    };

    return (
        <div className="mb-3 cg-policy-autocomplete" ref={wrapRef}>
            <Form.Group controlId="cg-policy-search">
            <Form.Label>Police</Form.Label>
            <Form.Control
                type="search"
                autoComplete="off"
                value={query}
                onChange={(e) => handleChange(e.target.value)}
                onFocus={() => setOpen(true)}
                placeholder="Rechercher par code police ou n° de réservation…"
                isInvalid={Boolean(error)}
                disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
            {open && (loading || options.length > 0 || query.trim().length >= 2) && (
                <ListGroup className="cg-policy-autocomplete__list">
                    {loading && (
                        <ListGroup.Item className="text-muted">
                            <Spinner size="sm" className="me-2" />
                            Recherche…
                        </ListGroup.Item>
                    )}
                    {!loading && options.length === 0 && query.trim().length >= 2 && (
                        <ListGroup.Item className="text-muted">Aucune police trouvée</ListGroup.Item>
                    )}
                    {!loading && options.map((option) => (
                        <ListGroup.Item
                            key={option.policy_id}
                            action
                            onClick={() => handlePick(option)}
                        >
                            <div className="fw-semibold">{option.policy_code || '—'}</div>
                            <div className="small text-muted">
                                {option.reservation_number || '—'}
                                {' · '}
                                {[option.first_name, option.last_name].filter(Boolean).join(' ') || '—'}
                            </div>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}
            </Form.Group>
        </div>
    );
};

export default PolicyAutocomplete;
